import { useEffect, useState, useCallback } from "react";
import defaults from "@/config/siteConfig";

const STORAGE_KEY = "qsm_site_config_v1";

function deepMerge(base, override) {
  if (!override || typeof override !== "object") return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const key of Object.keys(override)) {
    if (
      override[key] &&
      typeof override[key] === "object" &&
      !Array.isArray(override[key])
    ) {
      out[key] = deepMerge(base?.[key] ?? {}, override[key]);
    } else if (override[key] !== undefined && override[key] !== null) {
      out[key] = override[key];
    }
  }
  return out;
}

function readOverride() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function useSiteConfig() {
  const [config, setConfig] = useState(() => deepMerge(defaults, readOverride()));

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setConfig(deepMerge(defaults, readOverride()));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const save = useCallback((next) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setConfig(deepMerge(defaults, next));
      window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const reset = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setConfig(defaults);
  }, []);

  return { config, save, reset, defaults };
}

export default useSiteConfig;
